import * as idb from 'idb'
import objects from './objects';
import IDB_STORES from './stores'
import requestHandler, { setSimulateOffline } from './utils/request-handler';

const stores = {};
Object.keys(IDB_STORES).forEach(key => {
	stores[key] = IDB_STORES[key].name;
})

/**
 * @type {Promise<idb.IDBPDatabase<unknown>>}
 */
// هذه الوحدة تُستخدم من سياقين مختلفين في آن واحد (service worker و sync.js في الـ main thread)،
// لذا لا نستخدم deleteDB() هنا: إنشاء اتصال محذوف يبقى محظوراً (blocked) طالما هناك اتصال آخر
// مفتوح من السياق الآخر، وهو لا يُغلَق أبداً - ما يسبب توقّفاً كاملاً (deadlock) لا ينتهي أبداً.
// upgrade() هنا كافٍ وحده لإنشاء المتاجر عند أول فتح للقاعدة أو عند تغيير رقم الإصدار.
// مهم: يجب زيادة رقم الإصدار (DB_VERSION) عند إضافة أي متجر جديد إلى stores.js أو objects.js،
// وإلا فلن يُستدعى upgrade() مجدداً على قاعدة بيانات موجودة مسبقاً في متصفح المستخدم،
// فلا يُنشأ المتجر الجديد أبداً، وأي قراءة/كتابة عليه تفشل بصمت (NotFoundError).
const DB_VERSION = 3;
const dbPromise = idb.openDB('octopharma-api-cache', DB_VERSION, {
	upgrade(db) {
		Object.keys(IDB_STORES).forEach(storeKey => {
			if (!db.objectStoreNames.contains(IDB_STORES[storeKey].name)) {
				db.createObjectStore(IDB_STORES[storeKey].name, { keyPath: IDB_STORES[storeKey].keyPath });
			}
		})
		if (!db.objectStoreNames.contains('objects')) {
			db.createObjectStore('objects', { keyPath: 'key' });
		}
	}
})

/**
 * @param {String} key store name or object key
 * @param {Array|Object} data array to put in store or object to set in object key
 * @returns promise
 */
function write(key, data) {
	if (Array.isArray(data)) {
		return dbPromise.then(db => {
			const tx = db.transaction(key, 'readwrite');
			const store = tx.objectStore(key);
			// IndexedDB's getAll() يُعيد السجلات مرتّبة حسب قيمة المفتاح (keyPath) أبجدياً،
			// وليس حسب ترتيب الاستجابة الأصلي من الـ API - فيبدو الترتيب "مقلوباً" أو عشوائياً
			// عند القراءة. نُلحق رقم ترتيب صريح (__order) بكل سجل يفتقده، ونرتّب حسبه عند readAll.
			// لا يُستبدَل __order إن كان موجوداً مسبقاً (مثال: mergeEntities تُبقي على ترتيب
			// سجل معروف مسبقاً بدل نقله إلى آخر القائمة عند كل تحديث)
			const baseOrder = Date.now();
			const promises = data.map((item, i) => {
				const record = item.__order === undefined ? { ...item, __order: baseOrder * 1000 + i } : item;
				return store.put(record);
			});
			return Promise.all([...promises, tx.done]);
		})
	} else if (typeof data === 'object') {
		return dbPromise.then(db => {
			const tx = db.transaction('objects', 'readwrite');
			const store = tx.objectStore('objects');
			return Promise.all([store.put({ key, ...data }), tx.done]);
		})
	}
	return Promise.resolve();
}

/**
 * @param {String} objectKey
 * @returns promise
 */
function read(objectKey) {
	return dbPromise.then(db => {
		const tx = db.transaction('objects', 'readonly');
		const store = tx.store;
		return store.get(objectKey);
	})
}

/**
 * @param {String} storeName
 * @returns promise - النتيجة مرتّبة حسب __order (ترتيب الاستجابة الأصلي)، وليس حسب المفتاح
 */
function readAll(storeName) {
	return dbPromise.then(db => {
		const tx = db.transaction(storeName, 'readonly');
		const store = tx.store;
		return store.getAll();
	}).then(records => records.slice().sort((a, b) => (a.__order ?? 0) - (b.__order ?? 0)))
}

/**
 * @param {String} objectKey
 * @returns promise
 */
function remove(objectKey) {
	return dbPromise.then(db => {
		const tx = db.transaction('objects', 'readwrite');
		const store = tx.store;
		store.delete(objectKey);
		return tx.done;
	})
}

/**
 * @param {String} storeName
 * @returns promise
 */
function clearAll(storeName) {
	return dbPromise.then(db => {
		const tx = db.transaction(storeName, 'readwrite');
		tx.store.clear();
		return tx.done;
	})
}

/**
 * @returns promise
 */
function resetIDB() {
	const promises = [];
	Object.keys(IDB_STORES).forEach(storeKey => {
		promises.push(clearAll(IDB_STORES[storeKey].name));
	})
	promises.push(clearAll('objects'));
	return Promise.all(promises);
}

export default { write, read, readAll, remove, clearAll, resetIDB, requestHandler, setSimulateOffline, stores, objects };
